import { Skeleton } from '@mui/material';
import { Stack } from '@mui/system';
import { Customer, PatchInvoiceInput } from '@thro-types/billing';

import { Formik } from 'formik';
import { omit } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import { useGetInvoiceQuery, useInvoiceMutation } from 'api/hooks/billing/invoices';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { isCancelledInvoice } from '../../utils';
import { DetailBody } from './components/DetailBody';
import { invoiceInitialValues, invoiceValidationSchema } from './validation';

export const InvoiceDetailPage = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: invoiceId } = useParams<{ id: string }>();
  const [submitting, setSubmitting] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const { data: invoice, isLoading, isError } = useGetInvoiceQuery(invoiceId!);
  const { update } = useInvoiceMutation();
  const isInvoiceCancelled = !isLoading && isCancelledInvoice(invoice!);

  usePageHeading(
    {
      heading: isLoading
        ? t(translations.common.loading())
        : isInvoiceCancelled
        ? t(translations.pages.billing.cancelledInvoiceTitle())
        : t(translations.pages.billing.invoiceTitle()),
      headingProps: {
        back: location.state?.back ?? `/billing`
      }
    },
    [invoice]
  );

  useEffect(() => {
    if (isError) {
      navigate('/billing');
      dispatch(AlertActions.error(t(translations.pages.billing.alerts.loadingError())));
    }
  }, [isError, navigate, dispatch, t]);

  if (!invoiceId) {
    return;
  }

  const handleSave = async (values: PatchInvoiceInput & { customer?: Customer }) => {
    try {
      if (submitting) {
        return;
      }
      setSubmitting(true);
      setDisabledCancel(true);

      const omittedValues = omit(values, ['invoiceArticles', 'expertContactVersionId']);

      await update.mutateAsync({ id: invoiceId, data: omittedValues });
      dispatch(
        AlertActions.success(t(translations.pages.billing.alerts.successfulInvoiceUpdate()))
      );
    } catch (error) {
      dispatch(AlertActions.error(t(translations.pages.billing.alerts.failedInvoiceUpdate())));
    } finally {
      setSubmitting(false);
      setDisabledCancel(false);
    }
  };

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" width={180} height={40} />
        <Skeleton variant="rounded" width={450} height={40} />
        <Skeleton variant="rounded" width={250} height={40} />
        <Skeleton variant="rounded" width={450} height={40} />
        <Skeleton variant="rounded" width={450} height={40} />
      </Stack>
    );
  }

  if (!invoice) {
    return null;
  }

  return (
    <Formik
      onSubmit={handleSave}
      // @ts-ignore // TODO Fix types
      initialValues={invoiceInitialValues(invoice)}
      submittingState={[submitting, setSubmitting]}
      disabledCancel={disabledCancel}
      validationSchema={invoiceValidationSchema(t)}
      customSaveText="Save as Draft"
    >
      <DetailBody invoice={invoice} isCancelledInvoice={isInvoiceCancelled} />
    </Formik>
  );
};
